import { useState, useEffect, useMemo, useCallback } from 'react'
import type { Product, ProductPriceForm } from '../../types'
import { formatRupiah } from '../../types'
import { productApi } from '../../utils/api'

interface ProductFormData {
  name: string
  category: string
  prices: ProductPriceForm
  active: boolean
}

interface ProductFormErrors {
  name?: string
  category?: string
  prices?: string
}

const initialFormData: ProductFormData = {
  name: '',
  category: '',
  prices: { pcs: undefined, pack: undefined, halfPack: undefined },
  active: true,
}

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface Toast {
  id: number
  message: string
  type: ToastType
}

// Temporary / debug categories that must never appear in the admin filter.
// Real categories (e.g. "Makanan", "Minuman") are unaffected. Matching is
// case-insensitive so "Debug", "debug", "Test", "TEST", "Test Category",
// "test category", etc. are all excluded.
const HIDDEN_CATEGORY_VALUES = new Set(['debug', 'test', 'test category'])

function isHiddenCategory(value: string | null | undefined): boolean {
  if (!value) return false
  return HIDDEN_CATEGORY_VALUES.has(value.trim().toLowerCase())
}

export function AdminProductsPage() {
  const [products, setProducts] = useState<Product[]>([])
  const [showModal, setShowModal] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [formData, setFormData] = useState<ProductFormData>(initialFormData)
  const [errors, setErrors] = useState<ProductFormErrors>({})
  const [loading, setLoading] = useState(true)
  const [toasts, setToasts] = useState<Toast[]>([])
  const [isDeleting, setIsDeleting] = useState(false)

  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  // Separate confirmation states per action. The delete dialog NEVER reuses
  // the activate/deactivate dialog state — each action has an explicit type.
  type ConfirmDialogState =
    | { id: number; action: 'activate' | 'deactivate'; productName: string }
    | { id: number; action: 'delete'; productName: string }
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null)

  const addToast = (message: string, type: ToastType) => {
    const id = Date.now() + Math.random()
    setToasts((prev) => [...prev, { id, message, type }])
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 3500)
  }

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true)
      const res = await productApi.getAll()
      setProducts(res.data as Product[])
    } catch (err) {
      console.error('Failed to fetch products:', err)
      addToast('Gagal memuat data produk', 'error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProducts()
  }, [fetchProducts])

  const handleOpenModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product)
      setFormData({
        name: product.name,
        category: product.category || '',
        prices: {
          pcs: product.pricePcs || undefined,
          pack: product.pricePack || undefined,
          halfPack: product.priceHalfPack || undefined,
        },
        active: product.active,
      })
    } else {
      setEditingProduct(null)
      setFormData(initialFormData)
    }
    setErrors({})
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingProduct(null)
    setFormData(initialFormData)
    setErrors({})
  }

  const validateForm = (): boolean => {
    const newErrors: ProductFormErrors = {}
    if (!formData.name.trim()) newErrors.name = 'Nama produk wajib diisi'
    if (!formData.category.trim()) newErrors.category = 'Kategori wajib diisi'
    const hasPrice = Object.values(formData.prices).some((p) => p !== undefined && p !== null && p > 0)
    if (!hasPrice) newErrors.prices = 'Minimal satu harga harus diisi'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    try {
      const priceData = {
        pricePcs: formData.prices.pcs !== undefined && formData.prices.pcs > 0 ? formData.prices.pcs : null,
        pricePack: formData.prices.pack !== undefined && formData.prices.pack > 0 ? formData.prices.pack : null,
        priceHalfPack: formData.prices.halfPack !== undefined && formData.prices.halfPack > 0 ? formData.prices.halfPack : null,
      }

      if (editingProduct) {
        await productApi.update(editingProduct.id, {
          name: formData.name,
          category: formData.category,
          ...priceData,
          active: formData.active,
        })
        addToast('Produk berhasil diperbarui', 'success')
      } else {
        await productApi.create({
          name: formData.name,
          category: formData.category,
          ...priceData,
          active: formData.active,
        })
        addToast('Produk berhasil ditambahkan', 'success')
      }

      handleCloseModal()
      await fetchProducts()
    } catch (err) {
      setErrors({ prices: err instanceof Error ? err.message : 'Gagal menyimpan produk' })
      addToast(err instanceof Error ? err.message : 'Gagal menyimpan produk', 'error')
    }
  }

  const handleToggleActiveClick = (product: Product) => {
    setConfirmDialog({ id: product.id, action: product.active ? 'deactivate' : 'activate', productName: product.name })
  }

  const handleConfirmToggle = async () => {
    if (!confirmDialog || confirmDialog.action === 'delete') return
    try {
      await productApi.toggleActive(confirmDialog.id)
      const product = products.find((p) => p.id === confirmDialog.id)
      if (product) {
        addToast(
          product.active ? 'Produk dinonaktifkan' : 'Produk diaktifkan',
          'success'
        )
      }
      await fetchProducts()
    } catch (err) {
      addToast('Gagal mengubah status produk', 'error')
    }
    setConfirmDialog(null)
  }

  const handleDeleteClick = (product: Product) => {
    // Dedicated delete confirmation — MUST NOT trigger activate/deactivate.
    // Never call toggleActive() from here: this path performs a real
    // DELETE /api/products/admin/:id hard delete.
    setConfirmDialog({ id: product.id, action: 'delete', productName: product.name })
  }

  const handleConfirmDelete = async () => {
    if (!confirmDialog || confirmDialog.action !== 'delete') return
    const { id, productName } = confirmDialog
    setIsDeleting(true)
    try {
      // REAL HARD DELETE — permanently removes the Product record.
      await productApi.remove(id)
      // Remove immediately from the current list so counts/filters update
      // without waiting for the refetch.
      setProducts((prev) => prev.filter((p) => p.id !== id))
      setConfirmDialog(null)
      addToast(`Produk "${productName}" berhasil dihapus.`, 'success')
      await fetchProducts()
    } catch (err) {
      setConfirmDialog(null)
      addToast(err instanceof Error ? err.message : `Gagal menghapus produk "${productName}".`, 'error')
    } finally {
      setIsDeleting(false)
    }
  }

  const categories = useMemo(() => {
    const cats = new Set(
      products
        .map((p) => p.category)
        .filter((c): c is string => c !== null && c !== undefined && c.trim() !== '')
        .filter((c) => !isHiddenCategory(c))
    )
    return Array.from(cats).sort()
  }, [products])

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch = searchQuery === '' || product.name.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesCategory = categoryFilter === '' || product.category === categoryFilter
      const matchesStatus = statusFilter === '' || (statusFilter === 'active' ? product.active : !product.active)
      return matchesSearch && matchesCategory && matchesStatus
    })
  }, [products, searchQuery, categoryFilter, statusFilter])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner" aria-label="Memuat produk" />
      </div>
    )
  }

  const getToastIcon = (type: ToastType) => {
    if (type === 'success') {
      return (
        <svg className="w-5 h-5 shrink-0 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
    if (type === 'error') {
      return (
        <svg className="w-5 h-5 shrink-0 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
    if (type === 'warning') {
      return (
        <svg className="w-5 h-5 shrink-0 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      )
    }
    return (
      <svg className="w-5 h-5 shrink-0 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }

  const getToastClasses = (type: ToastType) => {
    // Strong solid colors, high contrast, immediately readable.
    switch (type) {
      case 'success':
        return 'bg-green-600 border-green-700'
      case 'error':
        return 'bg-red-600 border-red-700'
      case 'warning':
        return 'bg-amber-500 border-amber-600'
      case 'info':
        return 'bg-sky-600 border-sky-700'
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kelola Produk</h1>
          <p className="text-slate-500 mt-1">Tambah, edit, dan kelola produk toko</p>
        </div>
        <button onClick={() => handleOpenModal()} className="btn-primary">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          + Tambah Produk
        </button>
      </div>

      <div className="card p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label htmlFor="product-search" className="block text-sm font-medium text-slate-700 mb-1">
              Cari Produk
            </label>
            <input
              id="product-search"
              type="text"
              placeholder="Nama produk..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label htmlFor="category-filter" className="block text-sm font-medium text-slate-700 mb-1">
              Kategori
            </label>
            <select
              id="category-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="input"
            >
              <option value="">Semua Kategori</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="status-filter" className="block text-sm font-medium text-slate-700 mb-1">
              Status
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input"
            >
              <option value="">Semua Status</option>
              <option value="active">Aktif</option>
              <option value="inactive">Nonaktif</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchQuery('')
                setCategoryFilter('')
                setStatusFilter('')
              }}
              className="btn-secondary w-full"
            >
              Reset Filter
            </button>
          </div>
        </div>
      </div>

      {toasts.length > 0 && (
        <div
          aria-live="polite"
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            maxWidth: 'min(92vw, 380px)',
            width: 'max-content',
          }}
        >
          {toasts.map((toast) => (
            <div
              key={toast.id}
              role={toast.type === 'error' ? 'alert' : 'status'}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-xl border ${getToastClasses(toast.type)}`}
            >
              {getToastIcon(toast.type)}
              <span className="text-sm font-semibold text-white">{toast.message}</span>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        {filteredProducts.length === 0 ? (
              <div className="empty-state py-12">
                <svg className="empty-icon w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <p className="empty-text">
                  {(searchQuery || categoryFilter || statusFilter) ? 'Tidak ada produk yang cocok dengan filter' : 'Belum ada produk'}
                </p>
                <p className="empty-hint">
                  {(searchQuery || categoryFilter || statusFilter)
                    ? 'Coba ubah kata pencarian atau filter'
                    : 'Klik "+ Tambah Produk" untuk memulai'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Produk</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Kategori</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Harga PCS</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Harga HALF PACK</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Harga PACK</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Diupdate</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredProducts.map((product) => (
                      <tr key={product.id}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">{product.name}</p>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{product.category || '-'}</td>
                        <td className="px-4 py-3 text-slate-900">
                          {product.pricePcs ? formatRupiah(product.pricePcs) : '-'}
                        </td>
                        <td className="px-4 py-3 text-slate-900">
                          {product.priceHalfPack ? formatRupiah(product.priceHalfPack) : '-'}
                        </td>
                        <td className="px-4 py-3 text-slate-900">
                          {product.pricePack ? formatRupiah(product.pricePack) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`badge ${product.active ? 'badge-success' : 'badge-warning'}`}>
                            {product.active ? 'Aktif' : 'Non-Aktif'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-sm">
                          {product.updatedAt ? new Date(product.updatedAt).toLocaleDateString('id-ID', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          }) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            {product.active ? (
                              <button
                                onClick={() => handleToggleActiveClick(product)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-green-600 hover:bg-green-700 border border-green-700 transition-colors"
                                aria-label="Nonaktifkan produk"
                                title="Nonaktifkan produk"
                              >
                                <span aria-hidden="true" className="inline-block w-2 h-2 rounded-full bg-white" />
                                Aktif
                              </button>
                            ) : (
                              <button
                                onClick={() => handleToggleActiveClick(product)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-red-600 hover:bg-red-700 border border-red-700 transition-colors"
                                aria-label="Aktifkan produk"
                                title="Aktifkan produk"
                              >
                                <span aria-hidden="true" className="inline-block w-2 h-2 rounded-full bg-white" />
                                Non-Aktif
                              </button>
                            )}
                            <button
                              onClick={() => handleOpenModal(product)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 border border-blue-700 transition-colors"
                              aria-label="Edit produk"
                              title="Edit produk"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteClick(product)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-red-600 hover:bg-red-700 border border-red-700 transition-colors"
                              aria-label="Hapus produk"
                              title="Hapus produk"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Hapus
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

      <div className="mt-4 text-sm text-slate-500">
        Menampilkan {filteredProducts.length} dari {products.length} produk
        {(searchQuery || categoryFilter || statusFilter) && ' (difilter)'}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={handleCloseModal} role="dialog" aria-modal="true" aria-labelledby="product-modal-title">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h2 id="product-modal-title" className="text-lg font-semibold text-slate-900">
                {editingProduct ? 'Edit Produk' : 'Tambah Produk'}
              </h2>
              <button onClick={handleCloseModal} className="p-1 text-slate-500 hover:text-slate-700" aria-label="Tutup">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 max-h-[70vh] overflow-y-auto">
              <div className="space-y-4">
                <div>
                  <label htmlFor="product-name" className="block text-sm font-medium text-slate-700 mb-1">
                    Nama Produk <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="product-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`input ${errors.name ? 'border-red-500' : ''}`}
                    placeholder="Contoh: Indomie Goreng"
                    required
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="product-category" className="block text-sm font-medium text-slate-700 mb-1">
                    Kategori <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="product-category"
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className={`input ${errors.category ? 'border-red-500' : ''}`}
                    placeholder="Contoh: Mie Instan"
                    required
                  />
                  {errors.category && <p className="mt-1 text-sm text-red-500">{errors.category}</p>}
                </div>

                <fieldset className="border border-slate-200 rounded-lg p-4">
                  <legend className="text-sm font-medium text-slate-700 px-2">Harga (Rupiah)</legend>
                  <p className="text-xs text-slate-500 mb-3">Kosongkan jika tidak tersedia. Minimal satu harga harus diisi. Harga dalam IDR (integer).</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="price-pcs" className="block text-sm font-medium text-slate-700 mb-1">
                        PCS
                      </label>
                      <input
                        id="price-pcs"
                        type="number"
                        value={formData.prices.pcs ?? ''}
                        onChange={(e) => {
                          const val = e.target.value
                          setFormData({ ...formData, prices: { ...formData.prices, pcs: val !== '' ? parseInt(val, 10) : undefined } })
                        }}
                        className="input"
                        placeholder="Contoh: 3000"
                        min={0}
                      />
                    </div>
                    <div>
                      <label htmlFor="price-pack" className="block text-sm font-medium text-slate-700 mb-1">
                        1 Pack
                      </label>
                      <input
                        id="price-pack"
                        type="number"
                        value={formData.prices.pack ?? ''}
                        onChange={(e) => {
                          const val = e.target.value
                          setFormData({ ...formData, prices: { ...formData.prices, pack: val !== '' ? parseInt(val, 10) : undefined } })
                        }}
                        className="input"
                        placeholder="Contoh: 70000"
                        min={0}
                      />
                    </div>
                    <div>
                      <label htmlFor="price-half-pack" className="block text-sm font-medium text-slate-700 mb-1">
                        1/2 Pack
                      </label>
                      <input
                        id="price-half-pack"
                        type="number"
                        value={formData.prices.halfPack ?? ''}
                        onChange={(e) => {
                          const val = e.target.value
                          setFormData({ ...formData, prices: { ...formData.prices, halfPack: val !== '' ? parseInt(val, 10) : undefined } })
                        }}
                        className="input"
                        placeholder="Contoh: 35000"
                        min={0}
                      />
                    </div>
                  </div>
                  {errors.prices && <p className="mt-2 text-sm text-red-500">{errors.prices}</p>}
                </fieldset>

                <div className="flex items-center gap-2">
                  <input
                    id="product-active"
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="product-active" className="text-sm text-slate-700">
                    Produk Aktif (ditampilkan di halaman pesanan)
                  </label>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={handleCloseModal} className="btn-secondary">
                  Batal
                </button>
                <button type="submit" className="btn-primary">
                  {editingProduct ? 'Simpan Perubahan' : 'Tambah Produk'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDialog && confirmDialog.action !== 'delete' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setConfirmDialog(null)} role="dialog" aria-modal="true" aria-labelledby="confirm-dialog-title">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  confirmDialog.action === 'deactivate' ? 'bg-yellow-100' : 'bg-green-100'
                }`}>
                  {confirmDialog.action === 'deactivate' ? (
                    <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
                <div>
                  <h3 id="confirm-dialog-title" className="text-lg font-semibold text-slate-900">
                    {confirmDialog.action === 'deactivate' ? 'Nonaktifkan Produk' : 'Aktifkan Produk'}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {confirmDialog.action === 'deactivate'
                      ? 'Apakah Anda yakin ingin menonaktifkan produk ini? Produk akan disembunyikan dari halaman pesanan namun tetap tersimpan untuk referensi historis.'
                      : 'Apakah Anda yakin ingin mengaktifkan produk ini? Produk akan muncul kembali di halaman pesanan.'}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button onClick={() => setConfirmDialog(null)} className="btn-secondary">
                  Batal
                </button>
                <button onClick={handleConfirmToggle} className={confirmDialog.action === 'deactivate' ? 'btn-primary' : 'btn-primary'}>
                  {confirmDialog.action === 'deactivate' ? 'Nonaktifkan' : 'Aktifkan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDialog && confirmDialog.action === 'delete' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setConfirmDialog(null)} role="dialog" aria-modal="true" aria-labelledby="delete-dialog-title">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-red-100">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 id="delete-dialog-title" className="text-lg font-semibold text-slate-900">
                    Hapus Produk
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Apakah Anda yakin ingin menghapus produk &lsquo;{confirmDialog.productName}&rsquo;?
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Produk akan dihapus secara permanen dari daftar produk.
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button onClick={() => setConfirmDialog(null)} className="btn-secondary" disabled={isDeleting}>
                  Batal
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-bold text-white bg-red-600 hover:bg-red-700 border border-red-700 transition-colors disabled:opacity-60"
                >
                  {isDeleting ? 'Menghapus...' : 'Hapus Produk'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
